import { useState, useEffect } from 'react';
import { Play, Film } from 'lucide-react';
import { ContentRail } from './ContentRail';
import { formatDuration } from '../../utils/homeHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  player_story: 'Player Story',
  club_story: 'Club Story',
  competition_story: 'Competition',
  culture: 'Culture',
  rising_star: 'Rising Star',
};

const TYPE_COLORS = {
  player_story: 'bg-purple-600',
  club_story: 'bg-blue-600',
  competition_story: 'bg-emerald-600',
  culture: 'bg-amber-600',
  rising_star: 'bg-rose-600',
};

export function EditorialSection({ navigate, onPlayVideo }) {
  const [originals, setOriginals] = useState([]);

  useEffect(() => {
    fetch(`${API}/content/originals?limit=20`)
      .then(r => r.json())
      .then(data => setOriginals(data.originals || []))
      .catch(() => {});
  }, []);

  if (!originals.length) return null;

  // < 4 items: show 1 featured card
  if (originals.length < 4) {
    const featured = originals[0];
    return (
      <section className="py-10" data-testid="editorial-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <Film className="w-5 h-5 text-purple-400 shrink-0" />
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">AfroSports Originals</h2>
          </div>
          <div
            onClick={() => {
              if (featured.source === 'youtube' && featured.youtube_video_id) onPlayVideo(featured);
              else navigate(`/watch/${featured.id}`);
            }}
            className="relative rounded-2xl overflow-hidden cursor-pointer group max-w-3xl"
            data-testid="featured-original"
          >
            <img src={featured.thumbnail_url} alt={featured.title} loading="lazy" className="w-full aspect-[21/9] object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-purple-600 text-white text-[9px] font-bold rounded tracking-wider">AFROSPORTS ORIGINAL</span>
                {featured.original_type && (
                  <span className={`px-2 py-0.5 ${TYPE_COLORS[featured.original_type] || 'bg-purple-600'} text-white text-[9px] font-bold rounded`}>
                    {TYPE_LABELS[featured.original_type] || featured.original_type}
                  </span>
                )}
              </div>
              <h3 className="text-white text-2xl sm:text-3xl font-bold mb-2">{featured.title}</h3>
              {featured.description && <p className="text-gray-300 text-sm line-clamp-2 max-w-xl">{featured.description}</p>}
              <div className="flex items-center gap-3 mt-4">
                <button className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-white font-semibold text-sm transition-colors">
                  <Play className="w-4 h-4" fill="currentColor" /> Watch Now
                </button>
                {featured.duration && <span className="text-gray-400 text-sm">{formatDuration(featured.duration)}</span>}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ≥ 4 items: show horizontal rail
  return (
    <ContentRail
      icon={<Film className="w-5 h-5 text-purple-400 shrink-0" />}
      title="From AfroSports Originals"
      subtitle="Documentaries & stories celebrating African sport"
      seeAllPath="/originals"
      navigate={navigate}
      testId="editorial-section"
    >
      {originals.map((content) => (
        <div
          key={content.id}
          onClick={() => {
            if (content.source === 'youtube' && content.youtube_video_id) onPlayVideo(content);
            else navigate(`/watch/${content.id}`);
          }}
          className="flex-shrink-0 w-[260px] sm:w-[300px] cursor-pointer group snap-start"
          data-testid={`editorial-card-${content.id}`}
        >
          <div className="relative aspect-video rounded-xl overflow-hidden mb-2.5">
            <img src={content.thumbnail_url} alt={content.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            {/* Badges */}
            <div className="absolute top-2 left-2 flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[8px] font-bold rounded tracking-wider">AFROSPORTS ORIGINAL</span>
              {content.original_type && (
                <span className={`px-1.5 py-0.5 ${TYPE_COLORS[content.original_type] || 'bg-purple-600'} text-white text-[8px] font-bold rounded`}>
                  {TYPE_LABELS[content.original_type] || ''}
                </span>
              )}
            </div>
            {content.duration && (
              <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[9px] font-medium rounded">{formatDuration(content.duration)}</div>
            )}
            {/* Play overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-11 h-11 bg-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-purple-600/30">
                <Play className="w-4 h-4 text-white" fill="currentColor" />
              </div>
            </div>
          </div>
          <h3 className="text-white font-semibold text-sm group-hover:text-purple-400 transition-colors line-clamp-2">{content.title}</h3>
          <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">
            {content.duration ? formatDuration(content.duration) : ''}
            {content.original_type ? ` \u00B7 ${TYPE_LABELS[content.original_type] || content.original_type}` : ''}
          </p>
        </div>
      ))}
    </ContentRail>
  );
}
